import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ArrowUp, Check, Lock, Search, Tags, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { isPbrProtected } from '../../constants/pbr';
import type { NurseryCustomerTier, NurseryStockItem, NurseryTreeType, ProductPayload } from '../../types/nursery';

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const stockLabel = (si: Pick<NurseryStockItem, 'cultivar_name' | 'rootstock_name'>) =>
  [si.cultivar_name, si.rootstock_name].filter(Boolean).join(' / ');

/**
 * Create or edit an umbrella product: its name and tree type, price and tag
 * colour, and the stock items it is filled from, most preferred first.
 * Membership is chosen by hand — nothing about a rootstock or cultivar puts a
 * stock item in one — from the stock items of the product's tree type only.
 */
function ProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const productId = id ? Number(id) : null;
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', 'admin');

  const [loading, setLoading] = useState(true);
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [treeTypes, setTreeTypes] = useState<NurseryTreeType[]>([]);
  const [name, setName] = useState('');
  const [treeTypeId, setTreeTypeId] = useState<number | null>(null);
  // The product's own, kept in case its tree type has since been retired and
  // is no longer in the list.
  const [savedTreeType, setSavedTreeType] = useState<{ id: number; name: string } | null>(null);
  const [hasOrders, setHasOrders] = useState(false);
  const [skuCode, setSkuCode] = useState('');
  const [barcode, setBarcode] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [tagName, setTagName] = useState('');
  const [tagHex, setTagHex] = useState('');
  const [active, setActive] = useState(true);
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [tierIds, setTierIds] = useState<number[]>([]);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [stockRes, tierRes, typeRes, productRes, rulesRes] = await Promise.all([
          nurseryService.getStockItems(),
          nurseryService.getCustomerTiers(),
          nurseryService.getTreeTypes(),
          productId ? nurseryService.getProduct(productId) : Promise.resolve(null),
          productId ? nurseryService.getProductTierRules(productId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (stockRes.success) setStockItems(stockRes.data || []);
        if (tierRes.success) setTiers(tierRes.data || []);
        if (typeRes.success) setTreeTypes(typeRes.data || []);
        if (productRes) {
          if (!productRes.success) { setError(errorMessage(productRes, 'Could not load this product')); return; }
          const p = productRes.data;
          setName(p.name);
          setTreeTypeId(p.tree_type_id ?? null);
          if (p.tree_type_id != null) {
            setSavedTreeType({ id: p.tree_type_id, name: p.tree_type_name || p.tree_type_code || 'Tree type' });
          }
          setHasOrders(!!p.has_orders);
          setSkuCode(p.sku_code ?? '');
          setBarcode(p.barcode ?? '');
          setDescription(p.description ?? '');
          setPrice(p.website_price != null ? String(p.website_price) : '');
          setTagName(p.tag_colour_name ?? '');
          setTagHex(p.tag_colour_hex ?? '');
          setActive(p.active);
          setMemberIds(p.members.map((m) => m.stock_item_id));
        }
        if (rulesRes?.success) setTierIds(rulesRes.data || []);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const byId = useMemo(() => new Map(stockItems.map((si) => [si.id, si])), [stockItems]);
  const members = memberIds.map((mid) => byId.get(mid)).filter((si): si is NurseryStockItem => !!si);

  // A product is one tree type, chosen with its name: a 20cm Dwarf Eureka is
  // only ever filled from 20cm stock. The server refuses anything else.
  const treeTypeOptions = treeTypes.map((t) => ({ id: t.id, name: t.name }));
  if (savedTreeType && !treeTypeOptions.some((t) => t.id === savedTreeType.id)) treeTypeOptions.push(savedTreeType);
  const treeTypeLabel = treeTypeOptions.find((t) => t.id === treeTypeId)?.name ?? null;
  // Only a product saved before that rule can hold these.
  const wrongType = treeTypeId == null ? [] : members.filter((m) => m.tree_type_id !== treeTypeId);

  // Stock items of the old tree type cannot fill the new one.
  const chooseTreeType = (next: number | null) => {
    setTreeTypeId(next);
    setMemberIds((ids) => ids.filter((mid) => byId.get(mid)?.tree_type_id === next));
    setQuery('');
  };

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return stockItems
      .filter((si) => si.active && si.tree_type_id === treeTypeId && !memberIds.includes(si.id))
      .filter((si) => !q || [si.cultivar_name, si.cultivar_trade_name, si.rootstock_name, si.sku_code]
        .some((v) => v && v.toLowerCase().includes(q)))
      .slice(0, 30);
  }, [stockItems, memberIds, query, treeTypeId]);

  // Worth saying out loud before a customer is sold one of these.
  const protectedNames = [...new Set(members.filter((m) => isPbrProtected(m.cultivar_protection_status)).map((m) => m.cultivar_name))];

  const move = (index: number, by: -1 | 1) => {
    setMemberIds((ids) => {
      const next = [...ids];
      const [item] = next.splice(index, 1);
      next.splice(index + by, 0, item);
      return next;
    });
  };

  const save = async () => {
    setError(null);
    if (!name.trim()) { setError('Give the product a name'); return; }
    if (treeTypeId == null) { setError('Choose the tree type this product is sold as'); return; }
    if (memberIds.length === 0) { setError('Choose at least one stock item to fill it from'); return; }
    if (wrongType.length > 0) {
      setError(`Remove the stock items that are not ${treeTypeLabel}.`);
      return;
    }
    const priceNum = price.trim() === '' ? null : Number(price);
    if (priceNum != null && (Number.isNaN(priceNum) || priceNum < 0)) { setError('Enter a valid website price'); return; }

    const payload: ProductPayload = {
      name: name.trim(),
      tree_type_id: treeTypeId,
      sku_code: skuCode.trim() || null,
      barcode: barcode.replace(/\s+/g, '') || null,
      description: description.trim() || null,
      website_price: priceNum,
      tag_colour_name: tagHex ? (tagName.trim() || null) : null,
      tag_colour_hex: tagHex || null,
      active,
      stock_item_ids: memberIds,
    };
    setSaving(true);
    try {
      const res = productId
        ? await nurseryService.updateProduct(productId, payload)
        : await nurseryService.createProduct(payload);
      if (!res.success) { setError(errorMessage(res, 'Could not save the product')); return; }
      const rules = await nurseryService.setProductTierRules(res.data.id, tierIds);
      if (!rules.success) { setError(errorMessage(rules, 'Saved, but the tier visibility could not be saved')); return; }
      navigate('/nursery/products');
    } catch (e) {
      setError(errorMessage(e, 'Could not save the product'));
    } finally {
      setSaving(false);
    }
  };

  if (licenseLoading || loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Admin license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Products are part of the nursery catalogue setup and need an active{' '}
            <span className="font-medium">Nursery Admin</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/products')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={productId ? 'Edit Product' : 'New Product'}
        subtitle="One name and price over several stock items"
        icon={<Tags className="h-6 w-6 text-white" />}
      />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-4">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">What the customer buys</h3>
          <div>
            <label htmlFor="product-name" className="block text-sm font-medium text-neutral-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input id="product-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Dwarf Eureka Lemon" className={inputCls} />
          </div>
          <div>
            <label htmlFor="product-tree-type" className="block text-sm font-medium text-neutral-700 mb-1">
              Tree type <span className="text-red-500">*</span>
            </label>
            <select id="product-tree-type" value={treeTypeId ?? ''} disabled={hasOrders}
              onChange={(e) => chooseTreeType(e.target.value ? Number(e.target.value) : null)}
              className={`${inputCls} disabled:bg-neutral-50 disabled:text-neutral-500`}>
              <option value="">Choose a tree type…</option>
              {treeTypeOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <p className="text-xs text-neutral-500 mt-1">
              {hasOrders
                ? 'Fixed, because this product has been ordered. '
                : members.length > 0 ? 'Changing it removes the stock items below. ' : ''}
              Another tree type is another product, which can have the same name.
            </p>
          </div>
          <p className="text-xs text-neutral-500">
            The name and the tree type are all the customer sees — on the order, the docket, the invoice, their
            emails and the website.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="product-price" className="block text-sm font-medium text-neutral-700 mb-1">Website price (AUD)</label>
              <input id="product-price" type="number" min="0" step="0.01" value={price}
                onChange={(e) => setPrice(e.target.value)} placeholder="Not sold online" className={inputCls} />
            </div>
            <div>
              <label htmlFor="product-sku" className="block text-sm font-medium text-neutral-700 mb-1">SKU</label>
              <input id="product-sku" value={skuCode} onChange={(e) => setSkuCode(e.target.value)} maxLength={50} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="product-barcode" className="block text-sm font-medium text-neutral-700 mb-1">Barcode</label>
              <input id="product-barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} maxLength={30}
                inputMode="numeric" placeholder="e.g. 9358210298306" className={`${inputCls} sm:max-w-xs`} />
              <p className="text-xs text-neutral-500 mt-1">
                The number under the barcode on the tag. A retailer's purchase order names the tree by it, so an imported order finds
                this product by itself.
              </p>
            </div>
          </div>
          <div>
            <span className="block text-sm font-medium text-neutral-700 mb-1">Price-tag colour</span>
            <div className="flex flex-wrap items-center gap-2">
              <input type="color" aria-label="Tag colour" value={tagHex || '#1e88e5'}
                onChange={(e) => setTagHex(e.target.value.toLowerCase())}
                className="h-9 w-12 rounded border border-neutral-300 bg-white p-0.5" />
              <input aria-label="Tag colour name" value={tagName} onChange={(e) => setTagName(e.target.value)}
                placeholder="Colour name, e.g. Blue" maxLength={50} className={`${inputCls} flex-1 min-w-0 sm:max-w-xs`} />
              {tagHex ? (
                <button type="button" onClick={() => { setTagHex(''); setTagName(''); }}
                  className="text-xs text-neutral-500 hover:text-neutral-700">No tag colour</button>
              ) : (
                <span className="text-xs text-neutral-400">None set — pick a colour to use one</span>
              )}
            </div>
            <p className="text-xs text-neutral-500 mt-1">Named so it still reads on a black-and-white docket.</p>
          </div>
          <div>
            <label htmlFor="product-description" className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
            <textarea id="product-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              className={`${inputCls} resize-none`} />
          </div>
          {productId && (
            <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Still sold (untick to retire it; orders already taken are kept)
            </label>
          )}
        </section>

        <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Filled from</h3>
            <p className="text-xs text-neutral-500 mt-1">
              The stock items behind this product, first choice first. A new order line starts on the first
              one that is active; allocating and picking will take a lot of any of them.
            </p>
          </div>

          {members.length === 0 ? (
            <p className="text-sm text-neutral-500">
              {treeTypeId == null ? 'Choose a tree type above to see its stock items.' : 'None yet — add at least one below.'}
            </p>
          ) : (
            <ol className="space-y-2">
              {members.map((m, i) => (
                <li key={m.id} className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2">
                  <span className="text-xs font-semibold text-neutral-400 w-4">{i + 1}</span>
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="block font-medium text-neutral-800 break-words">{stockLabel(m)}</span>
                    {(m.sku_code || !m.active) && (
                      <span className="block text-xs text-neutral-500 break-words">
                        {[m.sku_code, !m.active ? 'inactive, skipped' : null].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  <button type="button" aria-label={`Move ${stockLabel(m)} up`} disabled={i === 0} onClick={() => move(i, -1)}
                    className="p-1 text-neutral-500 hover:text-neutral-800 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" aria-label={`Move ${stockLabel(m)} down`} disabled={i === members.length - 1} onClick={() => move(i, 1)}
                    className="p-1 text-neutral-500 hover:text-neutral-800 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" aria-label={`Remove ${stockLabel(m)}`} onClick={() => setMemberIds((ids) => ids.filter((x) => x !== m.id))}
                    className="p-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ol>
          )}

          {wrongType.length > 0 && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              This product is {treeTypeLabel}, and is filled only from {treeTypeLabel} stock items. Remove{' '}
              {wrongType.map((m) => `${stockLabel(m)} (${m.tree_type_name || m.tree_type_code})`).join(', ')} before
              saving, and make a separate product for {wrongType.length === 1 ? 'it' : 'them'}.
            </p>
          )}
          {protectedNames.length > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {protectedNames.join(', ')} {protectedNames.length === 1 ? 'is' : 'are'} PBR-protected. Protected varieties
              generally have to be sold under their registered name, so check before selling{' '}
              {protectedNames.length === 1 ? 'it' : 'them'} only as {name.trim() || 'this product'}.
            </p>
          )}

          {treeTypeId != null && (
            <div>
              <label htmlFor="product-add" className="block text-sm font-medium text-neutral-700 mb-1">
                Add a {treeTypeLabel} stock item
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                <input id="product-add" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search cultivar or rootstock..." className={`${inputCls} pl-9`} />
              </div>
              <ul className="mt-2 max-h-60 overflow-y-auto divide-y divide-neutral-100 rounded-lg border border-neutral-200">
                {candidates.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-neutral-400">
                    {!stockItems.some((si) => si.active && si.tree_type_id === treeTypeId)
                      ? `No ${treeTypeLabel} stock items yet. Add them under Stock Items first.`
                      : query.trim() ? `No other ${treeTypeLabel} stock item matches.` : `Every ${treeTypeLabel} stock item is already in.`}
                  </li>
                ) : candidates.map((si) => (
                  <li key={si.id}>
                    <button type="button" onClick={() => { setMemberIds((ids) => [...ids, si.id]); setQuery(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50">
                      <span className="font-medium text-neutral-800 break-words">{stockLabel(si)}</span>
                      {si.sku_code && <span className="text-neutral-500"> · {si.sku_code}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {tiers.length > 0 && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-2">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Who sees it</h3>
            <p className="text-xs text-neutral-500">
              {tierIds.length === 0
                ? 'Every customer tier. Tick tiers to show it to only those — say, retail and garden centres but not commercial growers.'
                : 'Only the ticked tiers.'}{' '}
              The stock items behind it keep their own visibility.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {tiers.map((t) => (
                <label key={t.id} className="inline-flex items-center gap-1.5 text-sm text-neutral-700">
                  <input type="checkbox" checked={tierIds.includes(t.id)}
                    onChange={() => setTierIds((ids) => ids.includes(t.id) ? ids.filter((x) => x !== t.id) : [...ids, t.id])} />
                  {t.name}
                </label>
              ))}
            </div>
          </section>
        )}

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className="flex flex-wrap justify-between gap-2">
          <Button variant="outline" type="button" onClick={() => navigate('/nursery/products')}>
            <ArrowLeft className="h-4 w-4" /> Cancel
          </Button>
          <Button variant="primary" type="button" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : productId ? 'Save product' : 'Create product'}
            {!saving && <Check className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </>
  );
}

export default ProductForm;
